import { DestroyRef, Injectable } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { PlatformInformationService } from '../../../../projects/platform-manager/src/public_api';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root'
})
export class CustomScrollRestorationService {
  private currentOffset: number[];
  private currentUrl: string;
  private trigger: string;
  constructor(
    private router: Router,
    private platformService: PlatformInformationService
  ) {}

  /**
   * enables application wide scroll restoration that's smart enough to not scroll when navigating named outlets
   * @param destroyRef application destroyRef to remove subscription from memory
   */
  setupScrollRestorationListener(destroyRef: DestroyRef) {
    if (this.platformService.getIsBrowser()) {
      // make sure 'back' navigation keeps scroll offset, no additional work from us needed for 'back' navigations
      // https://developer.mozilla.org/en-US/docs/Web/API/History/scrollRestoration
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
      // keep check on 'forward' navigations and decide if we want to 'scroll up'.
      // we basically always want to scroll up on 'forward navigations' except on non-base-router-outlet navigations
      // (don't scroll up on map pop-ups, don't scroll up on 'back button')
      this.router.events
        .pipe(
          filter(event => event instanceof NavigationStart || event instanceof NavigationEnd),
          takeUntilDestroyed(destroyRef)
        )
        .subscribe(event => {
          if (event instanceof NavigationStart) {
            // we are about to navigate. remember direction ('forward'), current offset, current url
            this.trigger = event.navigationTrigger;
            this.currentOffset = [window.scrollX, window.scrollY];
            this.currentUrl = this.router.url;
          } else {
            // we 'navigated'. Check where we need to scroll now.
            if (this.trigger === 'imperative') {
              requestAnimationFrame(() => {
                // we came or go to a named outlet: keep window offset how it was.
                // we only updated query params (?date=123 -> ?/date=456): keep offset
                if (
                  this.checkIfFromOrToNamedOutlet(event.urlAfterRedirects) ||
                  this.checkIfFromOrToNamedOutlet(this.currentUrl) ||
                  this.checkIfPathUnchanged(this.currentUrl, event.urlAfterRedirects)
                ) {
                  window.scrollTo(this.currentOffset[0], this.currentOffset[1]);
                } else {
                  // we went somewhere else entirely, start from the top
                  window.scrollTo(0, 0);
                }
              });
            }
          }
        });
    }
  }

  private checkIfFromOrToNamedOutlet(url: string): boolean {
    return url.includes('modal') || url.includes('sideBar');
  }

  private checkIfPathUnchanged(prev: string, curr: string): boolean {
    return prev.split('?')[0] === curr.split('?')[0];
  }
}
